import React from 'react';
import { ArrowRightIcon, TrendingUpIcon } from 'lucide-react';
import { Opportunity, opportunities } from '../data/demandData';
const priorityStyles: Record<Opportunity['priority'], string> = {
  High: 'bg-[#BB5E40] text-cream',
  Medium: 'bg-[#BB5E40]/12 text-[#94452E]',
  Watch: 'bg-ink/8 text-ink/60'
};
export function DemandOpportunity({
  selectedOwner
}: {
  selectedOwner: string;
}) {
  const rows = selectedOwner ? opportunities.filter(item => item.brand === selectedOwner) : opportunities;
  const totalRecovery = rows.reduce((sum, row) => sum + Number(row.recovery.replace(/[^0-9.]/g, '')), 0);
  return <section aria-labelledby="opportunity-heading" data-id="element-878" data-mp="h52nfslp4e60">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-879" data-mp="h1opcwk2er4d">
        <div data-id="element-880" data-mp="c6rgbpmschrh">
          <h2 id="opportunity-heading" className="font-display text-2xl font-medium tracking-tight" data-id="element-881" data-mp="c2tw10oh1k60">Where demand is missing</h2>
          <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-ink/60" data-id="element-882" data-mp="34i7r5b2uygq">
            Carli ranks live inventory by the gap between forecast and actual sell-through, so you can spend attention where it changes the outcome.
          </p>
        </div>
        {rows.length > 0 && <p className="text-sm text-ink/60" data-id="element-883" data-mp="3lmd8aexl0h4n">
            Incremental recovery available{' '}
            <span className="font-display text-xl font-semibold text-[#94452E]" data-id="element-884" data-mp="3qlg02xojwquo">
              ${totalRecovery.toLocaleString('en-US')}
            </span>
          </p>}
      </div>

      {rows.length === 0 ? <p className="mt-6 rounded-2xl border border-dashed border-ink/15 bg-paper px-6 py-14 text-center text-sm text-ink/55" data-id="element-885" data-mp="n59a1tz26s4d">
          No open demand opportunities for {selectedOwner}.
        </p> : <div className="mt-6 space-y-3" data-id="element-886" data-mp="d5s3zbfwac7v">
          {rows.map(row => {
        const gap = row.forecast - row.sellThrough;
        return <article key={row.id} className="rounded-2xl border border-ink/10 bg-paper p-5 shadow-sm sm:p-6" data-id="element-887" data-mp="2tx9yykqu8gl5">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between" data-id="element-888" data-mp="14e21iqngegn8">
                  <div className="min-w-0 flex-1" data-id="element-889" data-mp="zeyux1rts2d5">
                    <div className="flex flex-wrap items-center gap-2.5" data-id="element-890" data-mp="zga2lcoe5u9j">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${priorityStyles[row.priority]}`} data-id="element-891" data-mp="ungt6bhz9xr4">
                        {row.priority}
                      </span>
                      <h3 className="text-base font-semibold" data-id="element-892" data-mp="lobvf7e8q8g0">{row.title}</h3>
                      <span className="text-sm text-ink/50" data-id="element-893" data-mp="go7pucevey4k">{row.brand}</span>
                    </div>
                    <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-ink/65" data-id="element-894" data-mp="bol41ekz89k5">{row.reason}</p>

                    <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm" data-id="element-895" data-mp="6rcuu3ht3fv1">
                      <Stat label="Units live" value={row.units.toLocaleString('en-US')} data-id="element-896" data-mp="262cjwvtwfpo1" />
                      <Stat label="Days live" value={String(row.daysLive)} data-id="element-897" data-mp="2142n6gsj0h0b" />
                      <Stat label="Sell-through" value={`${row.sellThrough}%`} data-id="element-898" data-mp="29ll3y9drpsr9" />
                      <Stat label="Forecast" value={`${row.forecast}%`} data-id="element-899" data-mp="29jifhts9k7on" />
                      <Stat label="Gap" value={`${gap > 0 ? '−' : '+'}${Math.abs(gap)} pts`} tone={gap > 0 ? 'warn' : 'good'} data-id="element-900" data-mp="2dldgex6a8j1w" />
                    </div>

                    <div className="mt-4 h-1.5 w-full max-w-xl overflow-hidden rounded-full bg-ink/8" data-id="element-901" data-mp="2nd9fobk3uf7p">
                      <div className="h-full rounded-full bg-[#BB5E40]" style={{
                  width: `${Math.min(100, row.sellThrough / row.forecast * 100)}%`
                }} data-id="element-902" data-mp="1qmz04mqv9njn" />
                    </div>
                  </div>

                  <div className="shrink-0 rounded-xl border border-[#BB5E40]/20 bg-[#BB5E40]/[0.05] p-4 lg:w-72" data-id="element-903" data-mp="1zmvwlajhzqp9">
                    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-[#94452E]" data-id="element-904" data-mp="24n9rdqw3945c">
                      <TrendingUpIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-905" data-mp="3b22gomfl3d96" />
                      Carli recommends
                    </p>
                    <p className="mt-2 text-sm font-semibold leading-snug" data-id="element-906" data-mp="ssfwvqkf3onk">{row.action}</p>
                    <p className="mt-2 text-sm text-ink/60" data-id="element-907" data-mp="nu4pdmhlhq8i">Expected recovery <span className="font-semibold text-ink" data-id="element-908" data-mp="nvpjjazzz75u">{row.recovery}</span></p>
                    <button type="button" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-[#BB5E40] px-4 py-2 text-xs font-semibold text-cream transition-colors hover:bg-[#A34E33]" data-id="element-909" data-mp="11v7d03o8n8lj">
                      Build campaign
                      <ArrowRightIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-910" data-mp="1bph83t4kekzy" />
                    </button>
                  </div>
                </div>
              </article>;
      })}
        </div>}
    </section>;
}
function Stat({
  label,
  value,
  tone
}: {
  label: string;
  value: string;
  tone?: 'warn' | 'good';
}) {
  return <span className="flex flex-col" data-id="element-911" data-mp="1uj36z0mfamem">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink/45" data-id="element-912" data-mp="1ug75517gfdkw">{label}</span>
      <span className={`font-semibold tabular-nums ${tone === 'warn' ? 'text-[#94452E]' : tone === 'good' ? 'text-forest' : 'text-ink'}`} data-id="element-913" data-mp="1phdt7d7tfumu">{value}</span>
    </span>;
}